// SEO landing page for TopicTag browse at /tags/<slug>.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Pressable,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import PageSearchInput from '@/components/Inputs/PageSearchInput';
import ToolPageCopy from '@/components/Tools/ToolPageCopy';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import TopicResearchCallout from '@/components/Topics/TopicResearchCallout';
import { topicTagApiSsr } from '@/lib/topicTagApi';
import { normalizeTopicPageCopy, buildFaqJsonLd } from '@/utils/topic_page_copy';
import { BORDER_RADIUS } from '@/constants/config';
import { colorForSlug, formatCount } from '@/utils/agent_catalog';

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 350;
const SITE_URL = 'https://gab.ai';

const _staticTagCache = {};

export async function generateStaticParams() {
  // Rendered on demand (backend SSR meta + client SPA), not prerendered. There
  // are tens of thousands of tags — prerendering them all would hammer the API
  // at build time and emit unused HTML. /tags/:slug SEO is injected at request
  // time in backend/routes/appRoutes.js; indexed URLs (5+ topics) are discovered
  // via /json/topic-tags/sitemap.xml.
  return [];
}

const buildTagTitle = (tag, tagSlug, fallbackTitle) => {
  if (tag?.meta?.title) return tag.meta.title;
  const name = tag?.name || tagSlug;
  return name ? `${name} — ${fallbackTitle}` : fallbackTitle;
};

const buildTagDescription = (tag, fallback) => {
  if (tag?.meta?.description) return tag.meta.description;
  if (tag?.description) return tag.description;
  return fallback;
};

export default function TopicTagPage() {
  const params = useLocalSearchParams();
  const slugRaw = params?.slug;
  const tagSlug = typeof slugRaw === 'string'
    ? slugRaw
    : Array.isArray(slugRaw)
      ? slugRaw[0] ?? ''
      : '';
  const tagSlugNorm = tagSlug ? String(tagSlug).toLowerCase() : '';

  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, isMD, showsScrollIndicator } = useResponsiveness();

  const staticPayload = tagSlug
    ? (_staticTagCache[tagSlug] || _staticTagCache[tagSlugNorm])
    : null;

  const [topics, setTopics] = useState(staticPayload?.topics || []);
  const [topicTag, setTopicTag] = useState(staticPayload?.topicTag || null);
  const [pagination, setPagination] = useState(staticPayload?.pagination || null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(!staticPayload);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  /** HTTP status behind `loadError`; 404 means the tag really is gone. */
  const [loadErrorStatus, setLoadErrorStatus] = useState(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery('');
    setDebouncedQuery('');
  }, [tagSlug]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setDebouncedQuery('');
      return undefined;
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    if (!tagSlug) return undefined;

    const cached = !debouncedQuery
      ? (_staticTagCache[tagSlug] || _staticTagCache[tagSlugNorm])
      : null;
    setPage(1);
    setLoadError(null);
    setLoadErrorStatus(null);
    if (cached) {
      setTopics(Array.isArray(cached.topics) ? cached.topics : []);
      setTopicTag(cached.topicTag || null);
      setPagination(cached.pagination || null);
      setLoading(false);
    } else {
      setTopics([]);
      if (!debouncedQuery) {
        setTopicTag(null);
      }
      setPagination(null);
      setLoading(true);
    }

    (async () => {
      try {
        const res = await topicTagApiSsr.getTopicsBySlug(tagSlug, {
          page: 1,
          limit: PAGE_SIZE,
          q: debouncedQuery || undefined,
        });
        if (cancelled) return;
        setTopics(Array.isArray(res?.topics) ? res.topics : []);
        setTopicTag(res?.topicTag || null);
        setPagination(res?.pagination || null);
        setLoadError(null);
        setLoadErrorStatus(null);
      } catch (e) {
        if (!cancelled) {
          setTopics([]);
          setLoadError(e?.message || 'error');
          setLoadErrorStatus(e?.status ?? null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [tagSlug, debouncedQuery, tagSlugNorm]);

  const hasMore = useMemo(() => {
    if (!pagination) return false;
    return page < (pagination.totalPages || 1);
  }, [pagination, page]);

  const loadMore = useCallback(async () => {
    if (!tagSlug || loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const res = await topicTagApiSsr.getTopicsBySlug(tagSlug, {
        page: nextPage,
        limit: PAGE_SIZE,
        q: debouncedQuery || undefined,
      });
      const batch = Array.isArray(res?.topics) ? res.topics : [];
      setTopics((prev) => {
        const seen = new Set(prev.map((row) => row.id));
        return [...prev, ...batch.filter((row) => row?.id && !seen.has(row.id))];
      });
      setPagination(res?.pagination || pagination);
      setPage(nextPage);
    } catch {
      /* keep current list */
    } finally {
      setLoadingMore(false);
    }
  }, [tagSlug, loadingMore, hasMore, page, pagination, debouncedQuery]);

  const accent = useMemo(() => colorForSlug(tagSlug), [tagSlug]);
  const gridCols = isXS ? 1 : isMD ? 3 : 4;
  const totalCount = pagination?.total ?? topics.length;
  const hubTitle = t('pages.topics.title') || 'Topic Guides';
  const tagName = topicTag?.name || tagSlug;
  const canonicalPath = `/tags/${encodeURIComponent(tagSlug)}`;
  const metaTitle = buildTagTitle(topicTag, tagSlug, hubTitle);
  const metaDescription = buildTagDescription(
    topicTag,
    t('pages.topics.seoMetaFallback'),
  );
  const metaKeywords = topicTag?.meta?.keywords
    || [tagName, 'topic guides', 'cultural topics', 'Gab AI'].filter(Boolean).join(', ');

  const pageCopyBlocks = useMemo(
    () => normalizeTopicPageCopy(topicTag?.meta?.pageCopy, tagName),
    [topicTag, tagName],
  );

  const pageCopyPalette = useMemo(
    () => ({
      bg: `${accent}22`,
      border: `${accent}55`,
      text: colors.text_color_primary,
    }),
    [accent, colors.text_color_primary],
  );

  const structuredData = useMemo(() => {
    if (!topicTag) return null;

    const pageUrl = `${SITE_URL}${canonicalPath}`;
    const itemList = topics.slice(0, 100).map((row, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      url: `${SITE_URL}/start/${encodeURIComponent(row.slug)}`,
      name: row?.meta?.title || row?.name,
    }));

    const graph = [
      {
        '@type': 'CollectionPage',
        name: metaTitle,
        description: metaDescription,
        url: pageUrl,
        isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: SITE_URL },
        ...(itemList.length ? {
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: pagination?.total ?? itemList.length,
            itemListElement: itemList,
          },
        } : {}),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: hubTitle, item: `${SITE_URL}/topics` },
          { '@type': 'ListItem', position: 3, name: tagName, item: pageUrl },
        ],
      },
    ];

    const faq = buildFaqJsonLd(pageCopyBlocks);
    if (faq?.mainEntity?.length) {
      graph.push({ '@type': 'FAQPage', mainEntity: faq.mainEntity });
    }

    return { '@context': 'https://schema.org', '@graph': graph };
  }, [topicTag, topics, pagination, metaTitle, metaDescription, canonicalPath, tagName, hubTitle, pageCopyBlocks]);

  const styles = useMemo(() => StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.solid_color_primary },
    inner: {
      paddingHorizontal: isXS ? 16 : 24,
      paddingTop: 16,
      paddingBottom: 32,
      maxWidth: 1100,
      alignSelf: 'center',
      width: '100%',
      gap: 16,
    },
    grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
    card: {
      width: Platform.OS === 'web'
        ? `calc((100% - ${(gridCols - 1) * 10}px) / ${gridCols})`
        : `${100 / gridCols}%`,
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      padding: 12,
      gap: 8,
      ...(Platform.OS === 'web' ? { cursor: 'pointer' } : null),
    },
    avatar: {
      width: 40,
      height: 40,
      borderRadius: 10,
      backgroundColor: `${accent}22`,
    },
    loading: { paddingVertical: 48, alignItems: 'center' },
    loadMore: { marginTop: 8, alignSelf: 'center', minWidth: 220 },
    meta: { opacity: 0.7, marginTop: 10 },
    intro: {
      width: '100%',
      alignItems: 'center',
    },
    description: {
      opacity: 0.85,
      lineHeight: 26,
      maxWidth: 760,
      textAlign: 'center',
      alignSelf: 'center',
    },
    searchRow: { width: '100%', marginTop: 4 },
  }), [colors, isXS, gridCols, accent]);

  const showNotFound = !loading && loadError && !topicTag;
  // Only a confirmed 404 justifies noindex. A 500 or a dropped request also
  // lands here, and this page is indexed — telling a crawler to drop it because
  // one fetch failed is far more expensive than briefly leaving it indexable.
  const isConfirmedMissing = showNotFound && loadErrorStatus === 404;

  return (
    <View style={styles.container}>
      <MetaHead
        title={metaTitle}
        description={metaDescription}
        canonical={canonicalPath}
        keywords={metaKeywords}
        structuredData={structuredData}
        lastModified={topicTag?.updatedAt}
        noindex={isConfirmedMissing}
      />
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={showsScrollIndicator}
      >
        <View style={styles.inner}>
          <View style={styles.intro}>
            {!!topicTag?.description && (
              <Text tag="h2" size="l" weight="normal" style={styles.description}>
                {topicTag.description}
              </Text>
            )}
            {!loading && pagination?.total != null && (
              <Text type="p" style={styles.meta}>
                {formatCount(totalCount)} {totalCount === 1 ? 'topic' : 'topics'}
              </Text>
            )}
          </View>

          {!showNotFound && <TopicResearchCallout accent={accent} />}

          {!showNotFound && (
            <PageSearchInput
              value={query}
              onChangeText={setQuery}
              placeholder={t('pages.topics.searchPlaceholder')}
              catalogVariant
              noPadding
              containerStyle={styles.searchRow}
            />
          )}

          {loading ? (
            <View style={styles.loading}>
              <ActivityIndicator color={accent} />
            </View>
          ) : showNotFound ? (
            <Text type="p" style={{ opacity: 0.75 }}>{t('pages.topics.notFound')}</Text>
          ) : (
            <>
              {pageCopyBlocks.length > 0 && !debouncedQuery && (
                <ToolPageCopy blocks={pageCopyBlocks} palette={pageCopyPalette} isMD={!isXS} />
              )}

              {topics.length === 0 && debouncedQuery ? (
                <Text type="p" style={{ opacity: 0.75 }}>
                  {t('pages.topics.noResultsForQuery').replace('%QUERY%', debouncedQuery)}
                </Text>
              ) : (
              <View style={styles.grid}>
                {topics.map((row) => (
                  <Pressable
                    key={row.id}
                    style={styles.card}
                    onPress={() => row.slug && router.push(`/start/${encodeURIComponent(row.slug)}`)}
                  >
                    {row.avatarPath ? (
                      <Image source={{ uri: row.avatarPath }} style={styles.avatar} contentFit="cover" />
                    ) : (
                      <View style={styles.avatar} />
                    )}
                    <Text type="p" weight="bold" numberOfLines={2}>{row.name}</Text>
                  </Pressable>
                ))}
              </View>
              )}

              {hasMore && topics.length > 0 && (
                <LoadMoreButton
                  onPress={loadMore}
                  isLoading={loadingMore}
                  isLarge
                  style={styles.loadMore}
                />
              )}
            </>
          )}
        </View>
        <View style={{ paddingHorizontal: isXS ? 16 : 24 }}>
          <AppDashboardFooter />
        </View>
      </ScrollView>
    </View>
  );
}
